import { useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { Email } from '../api/types';
import type { MailboxList } from '../mail/useMailboxList';
import { useT } from '../i18n';
import { avatarColor, displayName, initials, isDraft, isFlagged, isUnread, shortDate, snippet } from '../mail/format';
import { DENSITY, type Density } from '../theme/tokens';
import { useTheme } from '../theme/store';

/* Three-line row: sender, subject, snippet. Locked in turn 1 of the design. */

/* A stable DOM id per message, so focus and aria references have something to point at. */
const rowId = (emailId: string) => `msg-${emailId}`;

/*
    Seçim hedefinin iki yüzü de aynı daireyi doldurur — hangisinin görüneceğine global.css'teki
    `.row-pick` kuralı karar verir. Üst üste durmaları bilinçli: yer değiştirirken satır oynamaz.
*/
const pickFace: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    borderRadius: 'var(--r-pill)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
};

function Tick({ color }: { color: string }) {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12.5l5.5 5.5L20 6.5" />
        </svg>
    );
}

function estimateRow(density: Density, base: number): number {
    const d = DENSITY[density];
    const lines = 2 + d.snippetLines;
    return d.padY * 2 + lines * (base * 1.45) + 4;
}

function Row({
    email,
    selected,
    checked,
    active,
    index,
    total,
    onOpen,
    onToggle,
    showRecipient,
}: {
    email: Email;
    selected: boolean;
    checked: boolean;
    /*
        Where the keyboard cursor sits. Usually the open message, but not always: with nothing
        open the first row carries it, because a roving tabindex with no 0 in it is a list the
        keyboard cannot enter at all. Measured before the fix — tab skipped straight past it.
    */
    active: boolean;
    /** Position in the whole folder, not in the rendered window — see the note on the list. */
    index: number;
    total: number;
    onOpen: () => void;
    /** `extend` is a shift-click: select everything between here and the last one. */
    onToggle: (extend: boolean) => void;
    /** Gönderilmiş klasöründeyiz: satır göndereni değil ALICIYI göstermeli. */
    showRecipient: boolean;
}) {
    const { t, lang } = useT();
    /*
        When the folder is listed as conversations the row stands for a group, and then it is the
        group that is unread — one unopened reply keeps the whole thing bold, which is what a
        person means by "I haven't read that". A flat row has neither field and falls back to its
        own flag.
    */
    const count = email.threadCount ?? 0;
    const unread = count ? (email.threadUnread ?? 0) > 0 : isUnread(email);
    const draft = isDraft(email);
    /*
        Zemini boyanmis satirda soluk metin bir basamak yukselir.

        Olculdu: --text-mute, satirin kendi zemininde (--surface2) 11.5 pikselde 4.84:1 veriyor —
        bardan yalnizca 0.34 yukarida. Satir boyandiginda (acik olan --sel, isaretli --accent-bg)
        zemin aciliyor ve tarih 4.24'e dusuyor. Bu, İ-49'da --sel notre cekilirken dogdu ve axe
        onu ilk HTML iletisi fixture'a girdigi kosuda yakaladi: o ana kadar koyu temada masaustu
        genisliginde ACIK bir satiri tarayan kontrol yoktu.

        Cozum zemini geri karartmak degil — o, ayrimi yeniden siliktirirdi. Bir basamak yukselen
        metin hem ayrimi hem okunurlugu koruyor.
    */
    const tinted = checked || selected;
    /*
        Gönderilmiş bir iletide gönderen HER ZAMAN okuyanın kendisidir — o sütun, kutu boyunca
        kişinin kendi adını tekrarlar ve hiçbir şey söylemez. Her posta istemcisi orada alıcıyı
        gösterir. Taslak zaten böyle davranıyordu; Gönderilenler'de aynısı eksikti.

        Alıcıya düşerken `to` boş çıkabilir: ölçüldü (2026-09-09, sanabel'in 6.603 gönderilmiş
        iletisi) — 4 iletide boş, yalnız Bcc ile gönderilmişler. Orada göndericiye dönülür.
        `displayName([])` "(unknown)" yazar, ve bir satırın bilmediğini kesin bir dille söylemesi
        yanlış bilgiden beterdir. Liste özeti cc/bcc taşımıyor (server Summary'de yok), o yüzden
        ara bir basamak denenmiyor.
    */
    const alicilar = draft || showRecipient ? email.to : null;
    const karsiTaraf = alicilar?.length ? alicilar : email.from;
    const name = displayName(karsiTaraf);
    const seed = karsiTaraf?.[0]?.email ?? name;

    return (
        <div
            data-testid="message-row"
            id={rowId(email.id)}
            /*
                A listbox rather than a list of buttons: the folder is one thing you move a cursor
                through, and that is also how the keyboard already worked. `aria-selected` carries
                the tick-box — the bulk selection — while the row the cursor is on is carried by
                real DOM focus, which survives virtualisation better than aria-activedescendant.
            */
            role="option"
            aria-selected={checked}
            /*
                Required here, not optional: the list is virtualised, so the DOM holds a window of
                perhaps ten rows out of hundreds. Without these a screen reader announces "3 of 10"
                in a folder of four hundred.
            */
            aria-posinset={index + 1}
            aria-setsize={total}
            tabIndex={active ? 0 : -1}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    onOpen();
                } else if (e.key === ' ') {
                    // Space selects, the way it does in any multi-select list. Nothing was bound
                    // to it, and without it aria-multiselectable would be a claim we do not honour.
                    e.preventDefault();
                    onToggle(e.shiftKey);
                }
            }}
            onClick={onOpen}
            draggable
            onDragStart={(e) => {
                // A private type, so the folder tree can tell a dragged message
                // from a dragged folder without inspecting the payload.
                e.dataTransfer.setData('application/x-webmail-message', email.id);
                e.dataTransfer.effectAllowed = 'move';
            }}
            style={{
                display: 'flex',
                // Şerit satırın tam boyunu kaplasın diye: flex-start olsaydı 3px'lik bir çizgi olurdu.
                alignItems: 'stretch',
                borderBottom: '1px solid var(--border)',
                background: checked ? 'var(--accent-bg)' : selected ? 'var(--sel)' : 'transparent',
                cursor: 'pointer',
                height: '100%',
                boxSizing: 'border-box',
            }}
        >
            {/*
                Everything the row says visually with weight, a dot and two icons. A person
                listening to the list hears none of that, and "unread" is the single most
                important thing about a row.
            */}
            <span className="vh">
                {[
                    unread ? t('list.stUnread') : null,
                    count > 1 ? t('list.stThread', { count: String(count) }) : null,
                    draft ? t('list.stDraft') : null,
                    email.hasAttachment ? t('list.stAttachment') : null,
                    isFlagged(email) ? t('list.stFlagged') : null,
                ]
                    .filter(Boolean)
                    .join('. ')}
            </span>

            {/*
                Okunmamış durumunun görsel taşıyıcısı artık soldaki 3px şerit. Nokta kaldırıldı:
                kalın yazıyla aynı şeyi ikinci kez söylüyordu ve her satırdan yatay yer yiyordu.
            */}
            <span
                aria-hidden="true"
                style={{
                    width: 3,
                    flex: '0 0 auto',
                    background: unread ? 'var(--accent)' : 'transparent',
                }}
            />

            <div
                style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    gap: 'var(--row-gap)',
                    alignItems: 'flex-start',
                    /*
                        Şerit soldan 3px yiyor ve sol iç boşluk onu geri veriyor: 3 + (padX-1)
                        sağdaki padX+2 ile eşit, yani satırın iki kenarı görsel olarak simetrik.
                    */
                    padding: 'var(--row-pad-y) calc(var(--row-pad-x) + 2px) var(--row-pad-y) calc(var(--row-pad-x) - 1px)',
                }}
            >
                {/*
                    Seçim hedefi avatarın kendisi — ayrı bir onay kutusu sütunu yok.

                    Kaldırılan kutu çizilmişti ve bu bilinçliydi: role="option" içinde gerçek bir
                    <input type="checkbox"> yardımcı teknolojinin yine de ulaştığı iç içe bir
                    denetim olur, axe bunu adlandırmıştı. Gerekçe burada da geçerli — bu da çizim,
                    durum satırın kendi aria-selected'ında yaşıyor, klavyede boşlukla dönüyor.

                    Kutuyu kaldırıp yerine bir şey koymamak FAREYLE çoklu seçimi tümden kapatırdı:
                    klavyede boşluk vardı, farenin başka kapısı yoktu. Hedefin avatara taşınması
                    hem teslimin istediği yatay yeri geri veriyor hem hedefi 13px'ten büyütüyor.
                    Hangi durumda ne göründüğü global.css'te.
                */}
                <div
                    data-testid="row-select"
                    data-checked={checked ? 'true' : 'false'}
                    data-hit
                    className="row-pick"
                    aria-hidden="true"
                    title={t('list.rowSelect')}
                    onClick={(e) => {
                        // The row's own click opens the message; selecting must not.
                        e.stopPropagation();
                        onToggle(e.shiftKey);
                    }}
                    style={{
                        position: 'relative',
                        width: 'var(--row-avatar)',
                        height: 'var(--row-avatar)',
                        flex: '0 0 auto',
                        borderRadius: 'var(--r-pill)',
                    }}
                >
                    {checked ? (
                        <span style={{ ...pickFace, background: 'var(--accent)' }}>
                            <Tick color="var(--on-accent)" />
                        </span>
                    ) : (
                        <>
                            <span
                                className="row-pick-face"
                                style={{
                                    ...pickFace,
                                    background: avatarColor(seed),
                                    color: '#fff',
                                    fontSize: 'var(--fs-xs)',
                                    fontWeight: 700,
                                }}
                            >
                                {initials(name)}
                            </span>
                            <span
                                className="row-pick-mark"
                                style={{ ...pickFace, background: 'var(--chip-bg)', border: '1px solid var(--border2)' }}
                            >
                                <Tick color="var(--text-dim)" />
                            </span>
                        </>
                    )}
                </div>

                <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <span
                            /*
                                Karşı tarafın adı. Testid var, çünkü bu satır Türkçe metinle
                                doğrulanamaz: `text-transform` görüneni değiştirir ve İ/ı büyük-küçük
                                dönüşümü Türkçe'de İngilizce'deki gibi davranmaz — kayıtlı tuzak.
                            */
                            data-testid="row-person"
                            dir="auto"
                            style={{
                                fontSize: 'var(--fs-sender)',
                                fontWeight: unread ? 700 : 500,
                                color: unread ? 'var(--text)' : 'var(--text-dim)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                            }}
                        >
                            {draft ? t('list.draftTo', { name }) : name}
                        </span>
                        {draft ? (
                            /*
                                Taslak artık gönderen adını sarıya boyamıyor, kendi rozetini
                                taşıyor: rengin tek başına anlattığı bir durum, adı okuyup rengi
                                görmeyen için hiç anlatılmamış demektir.

                                Yazı --text, çerçeve --amber. Teslimin önerdiği "--amber-bg üstünde
                                --amber yazı" açık temada 3.33:1 veriyor ve AA'yı geçmiyor —
                                teslimin kendi uyarısı.
                            */
                            <span
                                data-testid="draft-badge"
                                aria-hidden="true"
                                style={{
                                    flex: '0 0 auto',
                                    fontSize: 'var(--fs-2xs)',
                                    fontWeight: 600,
                                    letterSpacing: '0.4px',
                                    textTransform: 'uppercase',
                                    padding: '0 5px',
                                    borderRadius: 'var(--r-badge)',
                                    background: 'var(--amber-bg)',
                                    color: 'var(--text)',
                                    border: '1px solid var(--amber)',
                                }}
                            >
                                {t('list.stDraft')}
                            </span>
                        ) : null}
                        {count > 1 ? (
                            // Beside the name rather than at the end of the row: it belongs to the
                            // people in the conversation, and the right-hand edge holds the date.
                            <span
                                aria-hidden="true"
                                style={{
                                    flex: '0 0 auto',
                                    fontFamily: 'var(--font-mono)',
                                    fontSize: 'var(--fs-2xs)',
                                    lineHeight: 1.6,
                                    padding: '0 5px',
                                    borderRadius: 'var(--r-pill)',
                                    color: 'var(--text-dim)',
                                    border: '1px solid var(--border2)',
                                }}
                            >
                                {count}
                            </span>
                        ) : null}
                        <span style={{ flex: 1 }} />
                        <span
                            style={{
                                fontFamily: 'var(--font-mono)',
                                fontSize: 'var(--fs-xs)',
                                color: unread ? 'var(--accent-strong)' : tinted ? 'var(--text-dim)' : 'var(--text-mute)',
                                flex: '0 0 auto',
                            }}
                        >
                            {shortDate(email.receivedAt, lang === 'tr' ? 'tr-TR' : 'en-GB')}
                        </span>
                    </div>

                    {/*
                        Ataç ve yıldız birinci satırdan buraya indi. Orada tarihle ve konuşma
                        sayacıyla aynı köşeyi paylaşıyorlardı; uzun bir adda üçü birden sıkışıyor,
                        ad ise kısalıyordu. Konunun sağı ikisinin de gerçekten ait olduğu yer.
                    */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 3 }}>
                        <span
                            dir="auto"
                            style={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: 'var(--fs-subject)',
                                fontWeight: unread ? 600 : 400,
                                color: email.subject ? (unread ? 'var(--text)' : 'var(--text-dim)') : tinted ? 'var(--text-dim)' : 'var(--text-mute)',
                                fontStyle: email.subject ? 'normal' : 'italic',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {email.subject || t('list.noSubject')}
                        </span>
                        {email.hasAttachment ? (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text-mute)" strokeWidth="1.8" strokeLinecap="round" style={{ flex: '0 0 auto' }}>
                                <path d="M20 11.5l-8.4 8.4a5 5 0 0 1-7.1-7.1l8.5-8.5a3.3 3.3 0 1 1 4.7 4.7l-8.4 8.4a1.7 1.7 0 0 1-2.4-2.4l7.8-7.7" />
                            </svg>
                        ) : null}
                        {isFlagged(email) ? (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="var(--amber)" stroke="var(--amber)" strokeWidth="1.7" strokeLinejoin="round" style={{ flex: '0 0 auto' }}>
                                <path d="M5.2 21V4M5.2 4.5h12l-2.2 3.9 2.2 3.9h-12" />
                            </svg>
                        ) : null}
                    </div>

                    <div
                        dir="auto"
                        style={{
                            fontSize: 'var(--fs-snippet)',
                            color: tinted ? 'var(--text-dim)' : 'var(--text-mute)',
                            lineHeight: 1.45,
                            marginTop: 3,
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 'var(--snippet-lines)' as unknown as number,
                            overflow: 'hidden',
                        }}
                    >
                        {snippet(email.preview)}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Skeleton() {
    const bar = (w: string): React.CSSProperties => ({
        height: 9,
        width: w,
        borderRadius: 'var(--r-pill)',
        background: 'var(--chip-bg)',
    });
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'stretch',
                borderBottom: '1px solid var(--border)',
                height: '100%',
                boxSizing: 'border-box',
            }}
        >
            {/* Yüklenmemiş satır da gerçek satırla aynı ölçüde: şerit yeri + aynı iç boşluk. */}
            <span style={{ width: 3, flex: '0 0 auto' }} />
            <div
                style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    gap: 'var(--row-gap)',
                    alignItems: 'flex-start',
                    padding: 'var(--row-pad-y) calc(var(--row-pad-x) + 2px) var(--row-pad-y) calc(var(--row-pad-x) - 1px)',
                }}
            >
                <div
                    style={{
                        width: 'var(--row-avatar)',
                        height: 'var(--row-avatar)',
                        flex: '0 0 auto',
                        borderRadius: 'var(--r-pill)',
                        background: 'var(--chip-bg)',
                    }}
                />
                <div style={{ flex: 1, display: 'grid', gap: 7, paddingTop: 4 }}>
                    <span style={bar('38%')} />
                    <span style={bar('72%')} />
                </div>
            </div>
        </div>
    );
}

export default function MessageList({
    list,
    selectedId,
    selection,
    onOpen,
    onToggleSelect,
    showRecipient = false,
}: {
    list: MailboxList;
    selectedId: string | null;
    selection: Set<string>;
    onOpen: (id: string) => void;
    onToggleSelect: (id: string, index: number, extend: boolean) => void;
    /** Gönderilmiş klasörü: satırlar alıcıyı gösterir. Varsayılan false — başka her klasör göndereni gösterir. */
    showRecipient?: boolean;
}) {
    const { t } = useT();
    const parentRef = useRef<HTMLDivElement>(null);
    const density = useTheme((s) => s.density);
    const baseSize = useTheme((s) => s.baseSize);

    const rowHeight = estimateRow(density, baseSize);
    const virtualizer = useVirtualizer({
        count: list.total,
        getScrollElement: () => parentRef.current,
        estimateSize: () => rowHeight,
        overscan: 10,
    });

    const items = virtualizer.getVirtualItems();
    const first = items[0]?.index ?? 0;
    const last = items[items.length - 1]?.index ?? 0;

    useEffect(() => {
        if (list.total) list.ensureRange(first, last);
    }, [first, last, list]);

    /*
        Keyboard navigation moved the selection but never the list: after a screenful the person
        was steering something they could no longer see. Measured before the fix — five presses of
        the down arrow left the selected row 150 pixels below a 400-pixel window, with the scroll
        position still at zero.

        `align: auto` only scrolls when the row is actually out of view, so clicking a visible row
        does not make the list jump.
    */
    const activeIndex = selectedId ? list.ids.indexOf(selectedId) : -1;
    // With nothing open the cursor still has to be somewhere, or the list has no tab stop.
    const focusIndex = activeIndex >= 0 ? activeIndex : 0;
    useEffect(() => {
        if (activeIndex >= 0) virtualizer.scrollToIndex(activeIndex, { align: 'auto' });
    }, [activeIndex, virtualizer]);

    /*
        Focus follows the cursor, but only for somebody who is already in the list. Moving focus
        here on first paint would take it away from whatever the person was actually doing — the
        search box, most likely.
    */
    useEffect(() => {
        const root = parentRef.current;
        if (!root || activeIndex < 0) return;
        if (!root.contains(document.activeElement)) return;
        const el = selectedId ? root.querySelector<HTMLElement>(`#${CSS.escape(rowId(selectedId))}`) : null;
        if (el && el !== document.activeElement) el.focus({ preventScroll: true });
    }, [activeIndex, selectedId]);

    // Density changes the row height; the virtualiser must remeasure.
    useEffect(() => {
        virtualizer.measure();
    }, [rowHeight, virtualizer]);

    if (list.error) {
        return (
            <div style={{ padding: 20, color: 'var(--red)', fontSize: 'var(--fs-md)' }}>
                {list.error}
            </div>
        );
    }

    // Only once the server has said so: before its first answer the list is blank, not empty.
    if (!list.total && !list.loading && list.loaded) {
        return (
            <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-mute)', fontSize: 'var(--fs-md)' }}>
                <div className="kicker">// {t('app.off').toLowerCase()}</div>
                <div style={{ marginTop: 6 }}>{t('list.empty')}</div>
            </div>
        );
    }

    return (
        <div
            ref={parentRef}
            role="listbox"
            aria-label={t('list.aria')}
            aria-multiselectable
            style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', background: 'var(--surface2)' }}
        >
            {/* The two wrappers exist to position rows, not to mean anything: an option has to
                count as a child of the listbox, and a presentational element does not break that. */}
            <div role="presentation" style={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
                {items.map((item) => {
                    const id = list.ids[item.index] ?? null;
                    const email = id ? list.emails[id] : undefined;
                    return (
                        <div
                            key={item.key}
                            role="presentation"
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: item.size,
                                transform: `translateY(${item.start}px)`,
                            }}
                        >
                            {email ? (
                                <Row
                                    email={email}
                                    selected={email.id === selectedId}
                                    checked={selection.has(email.id)}
                                    active={item.index === focusIndex}
                                    index={item.index}
                                    total={list.total}
                                    onOpen={() => onOpen(email.id)}
                                    onToggle={(extend) => onToggleSelect(email.id, item.index, extend)}
                                    showRecipient={showRecipient}
                                />
                            ) : (
                                <Skeleton />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
